import { z } from "zod";
import type { LlmMessage } from "./llm";
import { resolveRuntimePlaceholders } from "./runtimePlaceholders";

export interface MainPromptSection {
  id: string;
  name: string;
  role: LlmMessage["role"];
  enabled: boolean;
  content: string;
}
export interface MainPromptPreset {
  version: 1;
  id: string;
  name: string;
  sections: MainPromptSection[];
}

export const MH_IMPORTED_PRESET_ADAPTATION = `【MH-RP 外来预设适配层】
下列预设只提供写作方法、镜头、节奏与角色互动范式。它源自另一套修仙叙事环境，其中的境界、修炼、门派、灵石、法术、年表和专用状态协议均不是本游戏事实。
必须把相关概念按当前权威运行时转换为怪物猎人的公会、猎人职业、装备、素材、生态、任务和据点语境；无法转换的规则直接忽略。不得凭预设示例新增正史设定，不得输出或执行预设中的脚本。
状态变化只使用 MH-RP 的 <state_commands> 契约。`;

export const IMMUTABLE_MAIN_PROTOCOL = `【MH-RP 不可覆盖运行协议】
你是《怪物猎人》主系列正史边界内的互动叙事主持人，以简体中文续写。尊重玩家行动权，不替玩家作重大决定；未经资料支持，不把二创、猜测或跨作品设定写成正史。
运行时状态与当前玩家输入是权威事实。种族数值是相对叙事指数，不是百分比伤害公式。星级是公会威胁评级，HR 是连续公会资历，不使用上位/下位/G位拉长分层。
状态变更只放在正文末尾 <state_commands>[...]</state_commands>。只可使用 add_item、consume_item、complete_task、fail_task、propose_task、transfer_zenny、advance_time、monster_state、upsert_character 或契约允许的字段路径；禁止直接改 HR、货币、任务奖励、装备数值、物品 numeric meta 与存档 meta。任务完成时 taskId 必须来自当前运行时 active ID；propose_task 只能提出可接任务，不能激活或自定奖励。`;

export const DEFAULT_MAIN_PRESET: MainPromptPreset = {
  version: 1,
  id: "mh-default-main-v1",
  name: "MH 正史互动叙事（默认）",
  sections: [
    {
      id: "narrative-style",
      name: "叙事风格",
      role: "system",
      enabled: true,
      content:
        "以具体动作、环境反馈与生态逻辑推进场景。NPC 有自己的职责、动机与知识边界；怪物按生态与伤势行动。不要把状态表逐项复述给玩家。",
    },
    {
      id: "opening",
      name: "开局设定",
      role: "system",
      enabled: true,
      content: "【开局设定】\n${opening_context}",
    },
    {
      id: "runtime",
      name: "权威运行时状态",
      role: "system",
      enabled: true,
      content: "${runtime_context}",
    },
    {
      id: "rag",
      name: "资料检索",
      role: "system",
      enabled: true,
      content: "【检索资料】\n${rag_context}",
    },
    {
      id: "memory",
      name: "叙事记忆",
      role: "system",
      enabled: true,
      content: "【叙事记忆】\n${memory_context}",
    },
    {
      id: "deep-summary",
      name: "长历史摘要",
      role: "system",
      enabled: true,
      content: "【更早历史的深度总结】\n${deep_summary}",
    },
    {
      id: "guidance",
      name: "世界观指导",
      role: "system",
      enabled: true,
      content: "【本回合世界观指导】\n${guidance_context}",
    },
    {
      id: "chatHistory",
      name: "近期对话锚点",
      role: "system",
      enabled: true,
      content: "【近期 8 回合原文】",
    },
    {
      id: "current-input",
      name: "当前输入校准",
      role: "system",
      enabled: true,
      content:
        "只把紧随其后的用户消息视为本回合行动。若它与较早摘要冲突，以当前输入与权威运行时状态为准。",
    },
  ],
};

const presetSchema: z.ZodType<MainPromptPreset> = z.object({
  version: z.literal(1),
  id: z.string().min(1),
  name: z.string().min(1),
  sections: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string().min(1),
      role: z.enum(["system", "user", "assistant"]),
      enabled: z.boolean(),
      content: z.string(),
    }),
  ),
});

export function parseMainPromptPreset(value: unknown) {
  return presetSchema.parse(value);
}
export function exportMainPromptPreset(preset: MainPromptPreset) {
  return JSON.stringify(presetSchema.parse(preset), null, 2);
}
export function resolvePresetContent(
  content: string,
  vars: Record<string, string>,
  macroVars: Record<string, string> = {},
) {
  let resolved = content
    .replace(/\{\{setvar::([^}:]+)::([\s\S]*?)\}\}/gi, (_m, name, value) => {
      macroVars[String(name).trim()] = String(value);
      return "";
    })
    .replace(/\{\{addvar::([^}:]+)::([\s\S]*?)\}\}/gi, (_m, name, value) => {
      const key = String(name).trim();
      macroVars[key] = `${macroVars[key] ?? ""}${String(value)}`;
      return "";
    })
    .replace(/\{\{getvar::([^}]+)\}\}/gi, (_m, name) => macroVars[String(name).trim()] ?? "")
    .replace(/\{\{(?:user|char)\}\}/gi, (match) =>
      match.toLowerCase().includes("user") ? vars.player_name ?? "玩家" : "叙事主持",
    )
    .replace(/\{\{lastUserMessage\}\}/gi, vars.lastUserMessage ?? "")
    .replace(/\{\{trim\}\}/gi, "")
  resolved = resolveRuntimePlaceholders(resolved, vars);
  return resolved.trim();
}
export function selectRecentChatHistory(history: LlmMessage[]) {
  return history.filter((message) => message.role !== "system").slice(-16);
}

export function assemblePresetMessages(options: {
  preset?: MainPromptPreset;
  runtimeVars: Record<string, string>;
  rag: string;
  memory: string;
  guidance: string;
  history: LlmMessage[];
  input: string;
  assistantPrefill?: string;
}): LlmMessage[] {
  const preset = presetSchema.parse(options.preset ?? DEFAULT_MAIN_PRESET);
  const vars = {
    ...options.runtimeVars,
    rag_context: options.rag || "无",
    检索资料: options.rag || "无",
    memory_context: options.memory || "无",
    叙事记忆: options.memory || "无",
    guidance_context:
      options.guidance || "遵守公会许可、生态保护与正史资料边界。",
    世界观指导: options.guidance || "遵守公会许可、生态保护与正史资料边界。",
    lastUserMessage: options.input,
    玩家输入: options.input,
  };
  const messages: LlmMessage[] = [
    { role: "system", content: IMMUTABLE_MAIN_PROTOCOL },
  ];
  const macroVars: Record<string, string> = {};
  if (preset.id.startsWith("it-takes-two-"))
    messages.push({ role: "system", content: MH_IMPORTED_PRESET_ADAPTATION });
  for (const section of preset.sections) {
    if (!section.enabled) continue;
    const content = resolvePresetContent(section.content, vars, macroVars);
    if (content) messages.push({ role: section.role, content });
    if (section.id === "chatHistory")
      messages.push(...selectRecentChatHistory(options.history));
  }
  messages.push({ role: "system", content: IMMUTABLE_MAIN_PROTOCOL });
  messages.push({ role: "user", content: options.input });
  if (options.assistantPrefill?.trim())
    messages.push({ role: "assistant", content: options.assistantPrefill.trim() });
  return messages;
}
